import { Link } from "react-router-dom";
import { Phone, ArrowRight, Check, MapPin, Hammer, Sparkles, Compass, CalendarDays } from "lucide-react";
import { BRAND, IMAGES, SERVICES, GALLERY } from "../data/site";

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="Modern open kitchen with large format tile"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Readability gradient left → right */}
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 via-stone-950/55 to-transparent" />
        <div className="grain-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-40 pb-28 min-h-[100svh] flex flex-col justify-center">
          <div className="max-w-3xl text-white">
            <p className="reveal uppercase text-xs tracking-[0.3em] text-[#E8A976] mb-8 font-semibold">
              Kitchen · Bath · Full-home remodeling
            </p>
            <h1 className="reveal delay-1 font-display font-light text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.98] tracking-tight">
              Craftsmanship that
              <span className="block italic text-[#E8A976]">lives with you.</span>
            </h1>
            <p className="reveal delay-2 mt-10 text-base sm:text-lg text-stone-200/90 max-w-xl leading-relaxed">
              Morgan Construction Group builds premium open-concept kitchens, modern
              bathrooms, and tile-forward living spaces across Oregon and Washington.
              {BRAND.yearsInBusiness} years of doing it right the first time.
            </p>
            <div className="reveal delay-3 mt-12 flex flex-wrap items-center gap-4">
              <a
                href={BRAND.phoneLink}
                data-testid="hero-phone-cta"
                className="inline-flex items-center gap-3 bg-[#9B4819] hover:bg-[#7A3813] text-white px-8 py-5 text-base font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-12px_rgba(155,72,25,0.7)]"
              >
                <Phone className="w-5 h-5" strokeWidth={2.25} />
                Call {BRAND.phoneDisplay}
              </a>
              <Link
                to="/contact#schedule"
                data-testid="hero-schedule-cta"
                className="inline-flex items-center gap-3 bg-white text-stone-900 hover:bg-stone-100 px-8 py-5 text-base font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
              >
                <CalendarDays className="w-5 h-5" />
                Schedule an appointment
              </Link>
              <Link
                to="/gallery"
                data-testid="hero-gallery-cta"
                className="inline-flex items-center gap-3 bg-white/10 hover:bg-white/20 border border-white/30 backdrop-blur-md text-white px-8 py-5 text-base font-medium tracking-wide transition-all duration-300"
              >
                View our work
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Hero stats strip */}
            <div className="reveal delay-4 mt-20 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/15 pt-10">
              <div>
                <div className="font-display text-3xl sm:text-4xl font-light text-white">
                  {BRAND.yearsInBusiness}+
                </div>
                <div className="uppercase text-[10px] tracking-[0.25em] text-stone-300 mt-2">
                  Years building
                </div>
              </div>
              <div>
                <div className="font-display text-3xl sm:text-4xl font-light text-white">
                  OR · WA
                </div>
                <div className="uppercase text-[10px] tracking-[0.25em] text-stone-300 mt-2">
                  Service area
                </div>
              </div>
              <div>
                <div className="font-display text-3xl sm:text-4xl font-light text-white">
                  100%
                </div>
                <div className="uppercase text-[10px] tracking-[0.25em] text-stone-300 mt-2">
                  Licensed · insured
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STATEMENT */}
      <section className="bg-stone-50 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <p className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold">
              About the work
            </p>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-stone-900 leading-[1.1]">
              We remodel homes across the Pacific Northwest with an obsessive eye
              for tile, light, and the way a room feels when you walk in.
            </h2>
          </div>
        </div>
      </section>

      {/* SERVICES — Bento */}
      <section data-testid="services-preview" className="py-24 md:py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold mb-4">
                Services
              </p>
              <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone-900 leading-[1.05] max-w-2xl">
                What we remodel.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="see-all-services"
              className="text-sm tracking-wide text-stone-900 border-b border-stone-900 hover:border-[#9B4819] hover:text-[#9B4819] pb-1 transition-colors"
            >
              See all services →
            </Link>
          </div>

          {/* Bento grid — 12 col */}
          <div className="grid grid-cols-12 gap-5 auto-rows-[14rem]">
            {/* Kitchen — large */}
            <Link
              to="/services"
              data-testid="service-card-kitchens"
              className="relative col-span-12 md:col-span-8 row-span-2 overflow-hidden group"
            >
              <img
                src={SERVICES[0].image}
                alt={SERVICES[0].title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/20 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-8 md:p-12 text-white">
                <div className="flex items-center gap-3">
                  <Hammer className="w-5 h-5 text-[#E8A976]" />
                  <span className="uppercase text-[11px] tracking-[0.25em] text-stone-200">
                    01 — Flagship
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-3xl md:text-5xl font-light leading-tight mb-4">
                    Kitchen Remodeling
                  </h3>
                  <p className="text-stone-200/90 text-sm md:text-base max-w-md leading-relaxed">
                    {SERVICES[0].summary}
                  </p>
                </div>
              </div>
            </Link>

            {/* Bathroom */}
            <Link
              to="/services"
              data-testid="service-card-bathrooms"
              className="relative col-span-12 md:col-span-4 row-span-1 overflow-hidden group bg-stone-900"
            >
              <img
                src={SERVICES[1].image}
                alt={SERVICES[1].title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/75 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6 md:p-8 text-white">
                <Sparkles className="w-5 h-5 text-[#E8A976] mb-3" />
                <h3 className="font-display text-2xl font-light">Bathrooms</h3>
              </div>
            </Link>

            {/* Living room */}
            <Link
              to="/services"
              data-testid="service-card-living"
              className="relative col-span-12 md:col-span-4 row-span-1 overflow-hidden group bg-stone-100"
            >
              <img
                src={SERVICES[2].image}
                alt={SERVICES[2].title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/75 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6 md:p-8 text-white">
                <Compass className="w-5 h-5 text-[#E8A976] mb-3" />
                <h3 className="font-display text-2xl font-light">Living & Flooring</h3>
              </div>
            </Link>

            {/* Full remodel — text card */}
            <Link
              to="/services"
              data-testid="service-card-full"
              className="relative col-span-12 md:col-span-12 row-span-1 overflow-hidden group bg-stone-900 text-white"
            >
              <div className="relative h-full flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-8 md:p-12">
                <div>
                  <p className="uppercase text-[11px] tracking-[0.25em] text-[#E8A976] mb-3">
                    04 — End-to-end
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl font-light">
                    Full-home renovations, managed for you.
                  </h3>
                </div>
                <span className="inline-flex items-center gap-2 text-sm tracking-wide text-stone-200 group-hover:text-[#E8A976] transition-colors">
                  Learn more <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST / 10 YEARS */}
      <section data-testid="trust-section" className="py-24 md:py-36 bg-stone-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="uppercase text-xs tracking-[0.3em] text-[#E8A976] mb-6 font-semibold">
              A decade of craftsmanship
            </p>
            <div className="flex items-start gap-6">
              <div className="font-display text-[12rem] sm:text-[18rem] lg:text-[22rem] font-light leading-[0.8] text-[#9B4819]">
                10
              </div>
              <div className="pt-6">
                <div className="font-display text-2xl sm:text-3xl text-white leading-tight">
                  Years
                </div>
                <div className="text-stone-400 text-sm tracking-wide mt-1">
                  in business
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[1.1] mb-8">
              Built on referrals, finished to the millimeter.
            </h2>
            <p className="text-stone-300 text-base sm:text-lg leading-relaxed mb-10 max-w-xl">
              Most of our projects come from neighbors, friends, and past clients. We
              show up on time, protect your home, and only hand over a project when it's
              finished the way we'd want it in ours.
            </p>
            <ul className="space-y-4">
              {[
                "Licensed, bonded & insured in Oregon and Washington",
                "One dedicated project lead — you always know who to call",
                "Fixed quotes, clear timelines, detailed scopes of work",
                "Premium materials: tile, stone, hardwood, custom cabinetry",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-stone-200">
                  <Check className="w-5 h-5 text-[#E8A976] shrink-0 mt-1" strokeWidth={2.25} />
                  <span className="text-sm sm:text-base">{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-12 flex items-center gap-6">
              <div className="flex items-center gap-2 text-stone-300 text-sm">
                <MapPin className="w-4 h-4 text-[#E8A976]" />
                Oregon & Washington
              </div>
              <a
                href={BRAND.phoneLink}
                data-testid="trust-phone-cta"
                className="inline-flex items-center gap-2 border-b border-white/60 hover:border-[#E8A976] pb-1 text-sm text-white hover:text-[#E8A976] transition-colors"
              >
                <Phone className="w-4 h-4" />
                {BRAND.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section data-testid="gallery-preview" className="py-24 md:py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold mb-4">
                Selected work
              </p>
              <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone-900 leading-[1.05] max-w-2xl">
                Recent remodels.
              </h2>
            </div>
            <Link
              to="/gallery"
              data-testid="see-full-gallery"
              className="text-sm tracking-wide text-stone-900 border-b border-stone-900 hover:border-[#9B4819] hover:text-[#9B4819] pb-1 transition-colors"
            >
              Full gallery →
            </Link>
          </div>

          <div className="grid grid-cols-12 gap-5">
            {GALLERY.slice(0, 5).map((img, idx) => (
              <div
                key={img.id}
                data-testid={`gallery-preview-${img.id}`}
                className={`group overflow-hidden ${
                  idx === 0
                    ? "col-span-12 md:col-span-8 row-span-2 aspect-[4/3] md:aspect-auto md:h-[540px]"
                    : idx === 1
                    ? "col-span-12 md:col-span-4 h-[260px]"
                    : "col-span-6 md:col-span-4 h-[260px]"
                }`}
              >
                <div className="relative w-full h-full">
                  <img
                    src={img.src}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                    <div className="text-white">
                      <p className="uppercase text-[10px] tracking-[0.25em] text-[#E8A976] mb-2">
                        {img.category}
                      </p>
                      <p className="text-sm font-medium">{img.title}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <img
          src={IMAGES.bathroom}
          alt="Modern bathroom remodel"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-950/75" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-28 md:py-40 text-center text-white">
          <p className="uppercase text-xs tracking-[0.3em] text-[#E8A976] mb-6 font-semibold">
            Start your project
          </p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05] max-w-4xl mx-auto">
            Ready to remodel?
            <span className="block italic text-[#E8A976]">Give us a call.</span>
          </h2>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <a
              href={BRAND.phoneLink}
              data-testid="final-phone-cta"
              className="inline-flex items-center gap-3 bg-[#9B4819] hover:bg-[#7A3813] text-white px-10 py-6 text-lg font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_50px_-12px_rgba(155,72,25,0.7)]"
            >
              <Phone className="w-6 h-6" strokeWidth={2.25} />
              {BRAND.phoneDisplay}
            </a>
            <Link
              to="/contact#schedule"
              data-testid="final-schedule-cta"
              className="inline-flex items-center gap-3 bg-white/10 hover:bg-white/20 border border-white/30 backdrop-blur-md text-white px-10 py-6 text-lg font-medium tracking-wide transition-all duration-300"
            >
              <CalendarDays className="w-6 h-6" />
              Schedule an appointment
            </Link>
          </div>
          <p className="text-stone-300 text-sm mt-8">
            Serving homeowners across {BRAND.serviceArea}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
